import { useAudit } from "../../../../contexts/auditContext";
import { useHookCache } from "../../../../lib/hookCache";
import { useControl } from "../../../../hooks/audit/useControl";
import { cn } from "../../../../lib/utils.ts";
import { toast } from "sonner";
import { useState, useMemo } from "react";
import { Button } from "../../../../components/ui/button.tsx";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Plus, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../../components/ui/dialog";
import { AuditSession } from "../../../../models/audit/auditSession.ts";
import { SampleUploadModal } from "./sampleUpload/SampleUploadModal";
import { PullSamplesFromOptroModal } from "./optroSamples/PullSamplesFromOptroModal";
import { useAuth } from "../../../../contexts/authContext";
import { hasPermission } from "../../../../lib/permissions";
import { permissionDeniedTooltip } from "../../../admin/roles/rolePermissionGroups";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";

export const AuditUploadSampleList = ({
  auditSession,
}: {
  auditSession: AuditSession;
}) => {
  const { createSourceRow, createSamples, controlSession } = useAudit();
  const { invalidate } = useHookCache();
  // Envelope's `control` is `DryControl` (no `keyReportItems`); use the bridge.
  const { data: hydratedControl } = useControl(controlSession?.controlId);
  const { workspaceMember } = useAuth();
  const canRunTest = hasPermission(workspaceMember, "run_test");
  const runTestDeniedTooltip = permissionDeniedTooltip("run_test");
  const [creating, setCreating] = useState(false);
  const [creatingKeyReportSamples, setCreatingKeyReportSamples] =
    useState(false);
  const [keyReportModalOpen, setKeyReportModalOpen] = useState(false);
  const [sampleUploadModalOpen, setSampleUploadModalOpen] = useState(false);
  const [optroModalOpen, setOptroModalOpen] = useState(false);
  const hasOptro = Boolean(controlSession?.control?.externalRef);

  const handleSamplesAdded = () => {
    // ⚠️ REVIEWERS: this handler is SHARED by the CSV/Excel upload AND the Optro
    // pull (empty-state entry point) — this change affects BOTH flows, not just Optro.
    // Refresh the envelope too (not just source-rows): a first upload creates new
    // procedure attributes, and the sample detail table reads its column headers +
    // order from `procedure.attributeLookup` on the envelope. Without this the
    // headers render blank / mis-ordered until a full page reload (pre-existing
    // bug, surfaced by the Optro feature).
    void invalidate(["procedure-session", auditSession.id]);
    void invalidate(["procedure-session", auditSession.id, "source-rows"]);
    if (controlSession) {
      void invalidate(["control-session", controlSession.id]);
    }
  };
  const [attributePairs, setAttributePairs] = useState<
    Array<{ id: string; name: string; value: string }>
  >([{ id: crypto.randomUUID(), name: "", value: "" }]);

  const keyReports = useMemo(() => {
    return (
      hydratedControl?.keyReportItems?.filter(
        (item) => item.type === "key_report",
      ) ?? []
    );
  }, [hydratedControl?.keyReportItems]);

  const addPair = () =>
    setAttributePairs((prev) => [
      ...prev,
      { id: crypto.randomUUID(), name: "", value: "" },
    ]);
  const updatePair = (id: string, key: "name" | "value", newValue: string) =>
    setAttributePairs((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [key]: newValue } : p)),
    );
  const removePair = (id: string) =>
    setAttributePairs((prev) => prev.filter((p) => p.id !== id));

  const handleCreateSourceRow = async () => {
    const nonEmptyPairs = attributePairs.filter(
      (p) => p.name.trim().length > 0,
    );
    if (nonEmptyPairs.length === 0) {
      toast.error("Add at least one attribute");
      return;
    }
    const names = nonEmptyPairs.map((p) => p.name.trim());
    const hasDuplicate = new Set(names).size !== names.length;
    if (hasDuplicate) {
      toast.error("Duplicate attribute names are not allowed");
      return;
    }
    const hasMissingValue = nonEmptyPairs.some((p) => p.value.trim() === "");
    if (hasMissingValue) {
      toast.error("Provide a value for each attribute");
      return;
    }

    const attributes = names;
    const data: Record<string, string | number | null> = {};
    for (const pair of nonEmptyPairs) {
      const raw = pair.value;
      if (raw.trim() === "") data[pair.name] = null;
      else data[pair.name] = raw;
    }

    try {
      setCreating(true);
      await createSourceRow({ auditSession, attributes, data });
      setAttributePairs([{ id: crypto.randomUUID(), name: "", value: "" }]);
    } catch {
      // error toasts handled in context
    } finally {
      setCreating(false);
    }
  };

  const handleAddKeyReportSamples = async () => {
    if (keyReports.length === 0) return;

    setCreatingKeyReportSamples(true);
    try {
      const data = keyReports.map((keyReport) => ({
        "Key Report": keyReport.name,
      }));

      await createSamples({
        auditSession,
        attributes: ["Key Report"],
        data,
      });
      setKeyReportModalOpen(false);
    } catch {
      // errors toasted in context
    } finally {
      setCreatingKeyReportSamples(false);
    }
  };

  return (
    <div>
      <div
        className={cn(
          "border border-dashed border-stone-200 p-8 bg-stone-50 flex flex-col items-center justify-center gap-4",
          "transition-colors duration-200",
          creating ||
            creatingKeyReportSamples ||
            auditSession?.sourceRowsState === "processing"
            ? "opacity-70"
            : "",
        )}
      >
        <div className="text-center">
          <p className="text-base font-medium mb-2">
            Upload a CSV/Excel of your sample list
          </p>
        </div>

        <div className="mb-4 flex gap-4 sm:flex-row flex-col">
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  variant="outline"
                  onClick={() => setSampleUploadModalOpen(true)}
                  disabled={
                    !canRunTest ||
                    creating ||
                    auditSession?.sourceRowsState === "processing" ||
                    creatingKeyReportSamples
                  }
                  className="flex items-center gap-2"
                  loading={auditSession?.sourceRowsState === "processing"}
                >
                  <Plus size={16} />
                  Add Samples From CSV/Excel
                </Button>
              </span>
            </TooltipTrigger>
            {!canRunTest && (
              <TooltipContent>{runTestDeniedTooltip}</TooltipContent>
            )}
          </Tooltip>

          {keyReports.length > 0 && (
            <Dialog
              open={keyReportModalOpen}
              onOpenChange={setKeyReportModalOpen}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="flex items-center gap-2"
                        disabled={
                          !canRunTest ||
                          creating ||
                          creatingKeyReportSamples ||
                          auditSession?.sourceRowsState === "processing"
                        }
                        loading={creatingKeyReportSamples}
                      >
                        <Plus size={16} /> Add a Sample For Each Key Report
                      </Button>
                    </DialogTrigger>
                  </span>
                </TooltipTrigger>
                {!canRunTest && (
                  <TooltipContent>{runTestDeniedTooltip}</TooltipContent>
                )}
              </Tooltip>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add samples for key reports</DialogTitle>
                  <DialogDescription>
                    This will create {keyReports.length} sample
                    {keyReports.length > 1 ? "s" : ""} with the attribute "Key
                    Report" set to each key report name.
                  </DialogDescription>
                </DialogHeader>
                <div className="max-h-[400px] overflow-y-auto">
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">
                      Preview of samples to be created:
                    </Label>
                    <div className="space-y-2">
                      {keyReports.map((keyReport, index) => (
                        <div
                          key={keyReport.id}
                          className="flex items-center gap-3 p-3 border border-stone-200 rounded-md bg-stone-50"
                        >
                          <div className="flex-1">
                            <div className="text-sm font-medium">
                              Sample {index + 1}
                            </div>
                            <div className="text-xs text-stone-600 mt-1">
                              <span className="font-medium">Key Report:</span>{" "}
                              {keyReport.name}
                            </div>
                            {keyReport.description && (
                              <div className="text-xs text-stone-500 mt-1">
                                {keyReport.description}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setKeyReportModalOpen(false)}
                    disabled={creatingKeyReportSamples}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddKeyReportSamples}
                    disabled={creatingKeyReportSamples}
                    loading={creatingKeyReportSamples}
                  >
                    Create {keyReports.length} Sample
                    {keyReports.length > 1 ? "s" : ""}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          <Dialog>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="flex items-center gap-2"
                      disabled={
                        !canRunTest ||
                        creating ||
                        creatingKeyReportSamples ||
                        auditSession?.sourceRowsState === "processing"
                      }
                    >
                      <Plus size={16} /> Manually Add Sample
                    </Button>
                  </DialogTrigger>
                </span>
              </TooltipTrigger>
              {!canRunTest && (
                <TooltipContent>{runTestDeniedTooltip}</TooltipContent>
              )}
            </Tooltip>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add a sample</DialogTitle>
                <DialogDescription>
                  Create a single sample with attribute name/value pairs
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label>Attributes</Label>
                    <p className="text-xs text-stone-500 mt-0.5">
                      Add attribute name/value pairs
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addPair}
                    disabled={
                      creating || auditSession?.sourceRowsState === "processing"
                    }
                    className="flex items-center gap-2"
                  >
                    <Plus size={16} /> Add Attribute
                  </Button>
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {attributePairs.map((pair) => (
                    <div
                      className="grid grid-cols-1 md:grid-cols-5 gap-2"
                      key={pair.id}
                    >
                      <div className="flex flex-col gap-1 md:col-span-2">
                        <Label htmlFor={`attr-name-${pair.id}`}>Name</Label>
                        <Input
                          id={`attr-name-${pair.id}`}
                          value={pair.name}
                          onChange={(e) =>
                            updatePair(pair.id, "name", e.target.value)
                          }
                          disabled={
                            creating ||
                            auditSession?.sourceRowsState === "processing"
                          }
                          placeholder="e.g. amount"
                        />
                      </div>
                      <div className="flex flex-col gap-1 md:col-span-2">
                        <Label htmlFor={`attr-value-${pair.id}`}>Value</Label>
                        <Input
                          id={`attr-value-${pair.id}`}
                          value={pair.value}
                          onChange={(e) =>
                            updatePair(pair.id, "value", e.target.value)
                          }
                          disabled={
                            creating ||
                            auditSession?.sourceRowsState === "processing"
                          }
                          placeholder="e.g. 123.45"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => removePair(pair.id)}
                          disabled={
                            creating ||
                            auditSession?.sourceRowsState === "processing"
                          }
                          className="px-2"
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={handleCreateSourceRow}
                  disabled={
                    creating || auditSession?.sourceRowsState === "processing"
                  }
                  loading={
                    creating || auditSession?.sourceRowsState === "processing"
                  }
                >
                  Create Sample
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {hasOptro && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="outline"
                    onClick={() => setOptroModalOpen(true)}
                    disabled={
                      !canRunTest ||
                      creating ||
                      creatingKeyReportSamples ||
                      auditSession?.sourceRowsState === "processing"
                    }
                    className="flex items-center gap-2"
                  >
                    <Plus size={16} /> Pull Samples From Optro
                  </Button>
                </span>
              </TooltipTrigger>
              {!canRunTest && (
                <TooltipContent>{runTestDeniedTooltip}</TooltipContent>
              )}
            </Tooltip>
          )}
        </div>
      </div>

      <SampleUploadModal
        open={sampleUploadModalOpen}
        onOpenChange={setSampleUploadModalOpen}
        auditSession={auditSession}
        onSuccess={handleSamplesAdded}
      />

      {hasOptro && controlSession && (
        <PullSamplesFromOptroModal
          open={optroModalOpen}
          onOpenChange={setOptroModalOpen}
          auditSession={auditSession}
          controlSession={controlSession}
          onSuccess={handleSamplesAdded}
        />
      )}
    </div>
  );
};
